import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCacheShare } from '../ForBusiness/Cacheshare/hooks/useCacheShare';
import CascadePicker from '../ForBusiness/Cacheshare/CascadePicker';
import { Badge, BmModal, Btn, Card, EmptyState, Field, Input, Loading, Overline, SearchBox } from '../ForBrands/ui/BrandUI';
import Icon from '../ForBrands/ui/icons';
import { API_BASE_URL } from '../../constants';
import { ageGroupOf, displayTeamName } from '../../utils/teamNames';

const CODES_URL = `${API_BASE_URL}/admin/team-codes/`;
// Team plans (users/team_plans_views.py): Premium for every vouched player
// of a team or its whole program, read on every entitlement read.
const PLANS_URL = `${API_BASE_URL}/admin/team-plans/`;

const SOURCE_LABEL = {
  cerebro: 'Cerebro',
  cache_teams: 'CACHE Partner',
  sportsradar: 'NCAA D-I',
  genius: 'Genius football',
};

// The picker's own label rules: partner teams are named as entered, Cerebro
// programs by name, SportRadar and Genius teams as "Market Name".
export const teamNameOf = (t) => {
  if (!t) return '';
  if (t.source === 'cache_teams' || t.source === 'cerebro') return t.name || '';
  return `${t.market || ''} ${t.name || ''}`.trim();
};

// What the admin endpoint needs to resolve the league the onboarding flows
// roster with (see backend users/team_codes.py league_for).
export const codeRequestFor = (t, filters = {}) => {
  if (!t) return null;
  const source = t.source || 'cerebro';
  const [competition, division] = String(t.leagueKey || '').split('|');
  const gender = filters.gender?.value || (t.srSport === 'wbb' ? 'F' : t.srSport === 'mbb' ? 'M' : '');
  let leagueLabel = '';
  if (source === 'cerebro') leagueLabel = [competition, division].filter(Boolean).join(' ');
  else if (source === 'cache_teams') leagueLabel = t.partner_program || t.cacheLeague || t.league_key || 'CACHE Partner';
  else if (source === 'sportsradar') {
    leagueLabel = [t.srSport === 'wbb' ? "NCAA Women's Basketball" : "NCAA Men's Basketball", t.conferenceAlias || t.conference].filter(Boolean).join(' · ');
  } else if (source === 'genius') leagueLabel = ['NCAA Football', t.conference].filter(Boolean).join(' · ');
  return {
    teamId: String(t.id ?? t.team_id ?? ''),
    teamName: teamNameOf(t),
    source,
    leagueKey: t.leagueKey || '',
    sportKey: t.srSport || '',
    league: t.cacheLeague || t.league_key || '',
    leagueLabel,
    gender,
    level: division || filters.subDivision?.name || '',
    city: t.market || t.city || t.location || '',
  };
};

const TH = 'whitespace-nowrap border-b border-bm-border px-3 py-2.5 text-left bm-label-xs uppercase tracking-[0.06em] text-bm-muted';

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const players = (n) => `${n} ${n === 1 ? 'player' : 'players'}`;

// The age groups a program code covers: "17U, 16U, 15U".
export const squadAges = (squads = []) => (Array.isArray(squads) ? squads : [])
  .map((s) => ageGroupOf(s)).filter(Boolean).join(', ');

// What a code lookup (admin/team-codes/) says about the program: the code
// covers every age group ('program' scope, its squads), the other listings
// of the team it also links (aliases), and the squad codes it replaced as
// the shown code, which still link their own squad (aliasCodes). Read from
// the payload or its code doc.
export const programOf = (current) => {
  const code = current?.code || {};
  const squads = current?.squads || code.squads || [];
  const aliases = current?.aliases || code.aliases || [];
  const aliasCodes = current?.aliasCodes || [];
  const scope = current?.scope || code.scope || (squads.length > 1 ? 'program' : 'team');
  return {
    scope,
    name: current?.programName || code.programName || '',
    squads: Array.isArray(squads) ? squads : [],
    aliases: Array.isArray(aliases) ? aliases : [],
    aliasCodes: Array.isArray(aliasCodes) ? aliasCodes : [],
  };
};

const aliasLabel = (a) => [a?.code, displayTeamName(a) || a?.teamId].filter(Boolean).join(' · ');

// The line the plan block opens with: what the team's code does for the
// athlete fee under the code rule (the server's answer for this code only).
export const codeFeeLine = (waiver, rule) => {
  if (!waiver) return '';
  if (waiver.waivesFee) return 'Players who join with this code get Premium.';
  if (waiver.staffIssued && rule === 'sponsored_only') return 'Codes do not waive the fee right now. A team plan does.';
  return "This is a coach's code. It follows the coach's plan, so it does not waive the fee.";
};

// The team plan block under a code: Off, or "Premium for every player on
// this team" (this squad or the whole program), an optional last day and a
// note, with how many vouched players it covers now. A plan set from
// another squad of the program shows here too and is changed in place.
export const TeamPlanBlock = ({ request, authHeaders, program, code }) => {
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [on, setOn] = useState(false);
  const [scope, setScope] = useState(null);
  const [endsOn, setEndsOn] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const requestKey = JSON.stringify(request);
  const plan = answer?.plan || null;

  const fill = useCallback((data) => {
    setAnswer(data || null);
    setOn(!!data?.plan);
    setScope(data?.plan?.scope || null);
    setEndsOn(data?.plan?.endsOn || '');
    setNote(data?.plan?.note || '');
  }, []);

  // Read once per team picked (never after a code is issued or rotated).
  useEffect(() => {
    const body = requestKey ? JSON.parse(requestKey) : null;
    if (!body) { setAnswer(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    axios.post(PLANS_URL, { ...body, action: 'lookup' }, authHeaders)
      .then((res) => { if (!cancelled) fill(res.data); })
      .catch(() => { if (!cancelled) setAnswer({ problem: 'Could not read the team plan.' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [requestKey, authHeaders, fill]);

  if (!request) return null;
  if (loading) return <Loading label="Checking for a team plan..." />;

  // The program choice: a program code, a program plan, or a squad of an
  // age group. A program plan set on another squad covers this one and is
  // narrowed only from that squad (the server refuses it from here: "this
  // squad" would mean the squad it was set on).
  const elsewhere = !!plan && !!answer && !answer.anchoredHere;
  const lockedScope = elsewhere && plan.scope === 'program';
  const programChoice = !lockedScope && (program.scope === 'program' || plan?.scope === 'program'
    || !!ageGroupOf({ leagueKey: request.leagueKey, league: request.league, level: request.level }));
  const chosenScope = lockedScope ? 'program' : scope || (program.scope === 'program' ? 'program' : 'team');
  const anchorName = elsewhere ? displayTeamName(plan) || plan.teamName || 'another squad' : '';
  const covered = Number(answer?.covered || 0);
  const waiver = answer?.code && answer.code.code === code?.code ? answer.code : null;
  const feeLine = codeFeeLine(waiver, answer?.codeRule);

  const save = async () => {
    if (!on) { setConfirmEnd(true); return; }
    setBusy(true);
    try {
      const body = { ...request, action: 'set', scope: (lockedScope || programChoice) ? chosenScope : 'team', endsOn, note };
      // The squads this view knows; with none, a program plan keeps its own.
      if (body.scope === 'program' && program.squads.length) body.squads = program.squads;
      if (plan) body.planId = plan.id;
      const res = await axios.post(PLANS_URL, body, authHeaders);
      fill(res.data);
      toast.success(`Team plan saved. ${players(Number(res.data?.covered || 0))} covered now.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Could not save the team plan.');
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    setConfirmEnd(false);
    setBusy(true);
    try {
      const res = await axios.post(PLANS_URL, { ...request, action: 'end', planId: plan.id }, authHeaders);
      fill(res.data);
      toast.success('Team plan ended.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not end the team plan.');
    } finally {
      setBusy(false);
    }
  };

  const option = (value, label, checked, onPick, name) => (
    <label
      key={value}
      className={`flex cursor-pointer items-start gap-3 rounded-[8px] border px-4 py-3 transition-colors ${
        checked ? 'border-bm-orange bg-bm-orange/5' : 'border-bm-border bg-white hover:bg-bm-subtle'
      }`}
    >
      <input type="radio" name={name} value={value} checked={checked} onChange={onPick} className="mt-1 accent-bm-orange" />
      <span className="bm-label-l text-bm-ink">{label}</span>
    </label>
  );
  const dirty = on !== !!plan || (on && plan && (chosenScope !== plan.scope || endsOn !== (plan.endsOn || '') || note !== (plan.note || '')));
  let whereLine = '';
  if (lockedScope) whereLine = ` Set on ${anchorName} for the whole program, so who it covers is changed there.`;
  else if (elsewhere) whereLine = ` Set on ${anchorName}.`;

  return (
    <div className="flex flex-col gap-3 rounded-[8px] border border-bm-border p-4" data-testid="team-plan">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <Overline>Team plan</Overline>
          <p className="mt-1 bm-body-s text-bm-muted">
            {plan ? `${players(covered)} covered now.` : `${players(covered)} on this squad would be covered.`}
            {whereLine}
          </p>
          {feeLine && <p className="mt-1 bm-body-s text-bm-muted">{feeLine}</p>}
        </div>
        {plan && <Badge tone="brand">On</Badge>}
      </div>
      {answer?.problem && <p className="bm-body-s text-bm-critical">{answer.problem}</p>}
      <div role="radiogroup" aria-label="Team plan" className="flex flex-col gap-2">
        {option('off', 'Off', !on, () => setOn(false), 'team-plan')}
        {option('on', 'Premium for every player on this team', on, () => setOn(true), 'team-plan')}
      </div>
      {on && (
        <div className="flex flex-col gap-3 pl-1">
          {programChoice && (
            <div role="radiogroup" aria-label="Who the plan covers" className="flex flex-col gap-2">
              {option('team', 'This squad', chosenScope === 'team', () => setScope('team'), 'team-plan-scope')}
              {option('program', program.squads.length > 1 ? `The whole program (${squadAges(program.squads)})` : 'The whole program',
                chosenScope === 'program', () => setScope('program'), 'team-plan-scope')}
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Last day (optional)" hint="Leave empty for no end date.">
              <Input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} aria-label="Last day" />
            </Field>
            <Field label="Note (optional)" hint="Only staff see it.">
              <Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Who pays, invoice, contact" aria-label="Note" />
            </Field>
          </div>
        </div>
      )}
      {dirty && (
        <div className="flex flex-wrap gap-2">
          <Btn size="sm" onClick={save} disabled={busy}>{busy ? 'Saving...' : on ? (plan ? 'Save team plan' : 'Turn on team plan') : 'End team plan'}</Btn>
        </div>
      )}
      {plan && (answer?.players || []).length > 0 && (
        <ul className="divide-y divide-bm-border rounded-[8px] border border-bm-border bg-white">
          {answer.players.map((p) => (
            <li key={p.email} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
              <span className="bm-body-m text-bm-ink">{p.name || p.email}</span>
              <span className="bm-body-s text-bm-muted">{displayTeamName(p) || p.teamName}</span>
            </li>
          ))}
        </ul>
      )}
      {confirmEnd && plan && (
        <BmModal
          title="End this team plan?"
          onClose={() => !busy && setConfirmEnd(false)}
          width={480}
          footer={(
            <>
              <Btn variant="secondary" onClick={() => setConfirmEnd(false)} disabled={busy}>Keep it</Btn>
              <Btn onClick={end} disabled={busy}>End plan</Btn>
            </>
          )}
        >
          <p className="bm-body-m text-bm-ink">
            {players(covered)}{plan.scope === 'program' ? ' on every squad of the program' : ''} lose the Premium it
            includes right away, unless a coach&apos;s plan or a code still covers them.
          </p>
        </BmModal>
      )}
    </div>
  );
};

// Another listing for a code, picked in a cascade of its own (D14): an
// alias, another entry of the same real team such as an older listing (an
// athlete on it who enters the code is moved onto the code's team;
// 'alias_add', the listing's league as the code lookup resolves it, so the
// alias names that one squad), or, for a program code, an age group its
// discovery missed ('squad_add'). Both are admin/team-codes/ code actions.
export const CodeListingPicker = ({ kind, code, teamName, authHeaders, onDone, onClose }) => {
  const { filters, setFilters, teams, isLoadingTeams } = useCacheShare({ team: null });
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const squad = kind === 'squad';
  const pick = useMemo(() => (picked?.id ? codeRequestFor(picked, filters) : null), [picked, filters]);
  const name = pick
    ? displayTeamName({ teamName: pick.teamName, leagueKey: pick.leagueKey, league: pick.league }) || pick.teamName || pick.teamId
    : '';

  const save = async () => {
    if (!pick || busy) return;
    setBusy(true);
    try {
      if (squad) {
        await axios.post(CODES_URL, { action: 'squad_add', code, teamId: pick.teamId, source: pick.source,
          leagueKey: pick.leagueKey, league: pick.league, teamName: pick.teamName, leagueLabel: pick.leagueLabel }, authHeaders);
      } else {
        const found = await axios.post(CODES_URL, { ...pick, action: 'lookup' }, authHeaders);
        await axios.post(CODES_URL, { action: 'alias_add', code, aliasTeamId: pick.teamId,
          aliasLeague: found.data?.league || pick.league || '', aliasTeamName: pick.teamName }, authHeaders);
      }
      toast.success(squad ? `${name} is an age group of ${code} now.` : `${name} now links with ${code}.`);
      onDone();
    } catch (err) {
      setBusy(false);
      toast.error(err?.response?.data?.message || err?.response?.data?.error
        || (squad ? 'Could not add the age group.' : 'Could not link this listing.'));
    }
  };

  return (
    <BmModal
      title={squad ? 'Add a missing age group' : 'Link another listing'}
      onClose={() => !busy && onClose()}
      width={560}
      footer={(
        <>
          <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn onClick={save} disabled={!pick || busy}>
            {busy ? 'Saving...' : `${squad ? 'Add' : 'Link'} ${name || (squad ? 'age group' : 'listing')}`}
          </Btn>
        </>
      )}
    >
      <div className="flex flex-col gap-4">
        <p className="bm-body-m text-bm-muted">
          {squad
            ? `Pick the squad of ${teamName} that ${code} does not list yet. Players on it can then pick it when they enter the code.`
            : `Pick another entry of ${teamName}, such as an older listing of the same team. Athletes on it who enter ${code} are moved onto ${teamName}.`}
        </p>
        <CascadePicker
          filters={filters}
          onFiltersChange={setFilters}
          teams={teams}
          selectedTeam={picked}
          onTeamSelect={setPicked}
          isLoadingTeams={isLoadingTeams}
          layout="sidebar"
          variant="v2"
        />
      </div>
    </BmModal>
  );
};

const CodeChip = ({ code, onCopy, copied }) => (
  <button
    type="button"
    onClick={() => onCopy(code)}
    title="Copy code"
    className="inline-flex items-center gap-2 rounded-[8px] border border-bm-border bg-white px-3 py-1.5 font-mono text-[15px] tracking-[0.2em] text-bm-ink hover:border-bm-orange"
  >
    {code}
    <span className="bm-label-xs tracking-normal text-bm-muted">{copied === code ? 'Copied' : <Icon name="copy" size={13} />}</span>
  </button>
);

const TeamCodesPanel = ({ authHeaders }) => {
  const { filters, setFilters, teams, selectedTeam, setSelectedTeam, isLoadingTeams } = useCacheShare({ team: null });
  const [current, setCurrent] = useState(null);
  const [loadingCode, setLoadingCode] = useState(false);
  const [working, setWorking] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [issued, setIssued] = useState([]);
  const [loadingIssued, setLoadingIssued] = useState(false);
  const [q, setQ] = useState('');
  const [copied, setCopied] = useState('');
  // 'alias' | 'squad' while the listing picker is open; the alias whose
  // unlinking waits for a confirm.
  const [listing, setListing] = useState(null);
  const [unlinking, setUnlinking] = useState(null);

  const request = useMemo(() => codeRequestFor(selectedTeam, filters), [selectedTeam, filters]);
  const requestKey = JSON.stringify(request);
  // The team as people read it: no season suffix, the squad's age group.
  const teamTitle = request
    ? displayTeamName({ teamName: request.teamName, leagueKey: request.leagueKey, league: request.league }) || request.teamName
    : '';

  const loadIssued = useCallback(async () => {
    setLoadingIssued(true);
    try {
      const res = await axios.get(CODES_URL, { ...authHeaders, params: q ? { q } : {} });
      setIssued(res.data?.codes || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not load the issued codes.');
    } finally {
      setLoadingIssued(false);
    }
  }, [authHeaders, q]);

  useEffect(() => {
    const t = setTimeout(loadIssued, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [loadIssued, q]);

  useEffect(() => {
    const body = requestKey ? JSON.parse(requestKey) : null;
    if (!body) { setCurrent(null); return undefined; }
    let cancelled = false;
    setLoadingCode(true);
    axios.post(CODES_URL, { ...body, action: 'lookup' }, authHeaders)
      .then((res) => { if (!cancelled) setCurrent(res.data); })
      .catch((err) => {
        if (cancelled) return;
        setCurrent({ code: null, athletes: [], problem: err?.response?.data?.error || 'Could not look up this team.' });
      })
      .finally(() => { if (!cancelled) setLoadingCode(false); });
    return () => { cancelled = true; };
  }, [requestKey, authHeaders]);

  // Issue or regenerate. `scope` 'team' issues a squad-only code for an age
  // group of a program (D14: one code for every age group is the default).
  const act = async (action, scope = '') => {
    if (!request) return;
    setWorking(true);
    try {
      const res = await axios.post(CODES_URL, { ...request, action, ...(scope ? { scope } : {}) }, authHeaders);
      setCurrent(res.data);
      toast.success(action === 'regenerate'
        ? `New code ${res.data?.code?.code}. The old code no longer works.`
        : `Code ${res.data?.code?.code} issued for ${teamTitle || request.teamName}.`);
      loadIssued();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not issue the code.');
    } finally {
      setWorking(false);
      setConfirmRegen(false);
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied((c) => (c === code ? '' : c)), 1500);
    } catch {
      toast.error('Copy failed. Select the code and copy it by hand.');
    }
  };

  // Athletes on the program who joined before the code existed: email them
  // the code, or verify one on their squad (admin/team-codes/ actions
  // 'email_code' {code, emails} and 'verify' {code, athleteEmail}; the
  // server finds each athlete's squad). The list is read again after each,
  // and after a listing is linked or unlinked.
  const reload = async () => {
    if (!request) return;
    try {
      const res = await axios.post(CODES_URL, { ...request, action: 'lookup' }, authHeaders);
      setCurrent(res.data);
    } catch { /* the list stays as it was */ }
  };
  const emailCode = async (rows) => {
    if (!current?.code?.code) return;
    setWorking(true);
    try {
      const res = await axios.post(CODES_URL, { action: 'email_code', code: current.code.code,
        emails: rows.map((a) => a.email) }, authHeaders);
      const sent = Array.isArray(res.data?.sent) ? res.data.sent.length : null;
      if (sent === null) throw new Error('not sent');
      if (sent === 0) toast.error('No email went out. Those athletes are no longer waiting on this code.');
      else toast.success(`Code emailed to ${sent} ${sent === 1 ? 'athlete' : 'athletes'}.`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Could not email the code.');
    } finally {
      setWorking(false);
    }
  };
  const verifyOne = async (row) => {
    if (!current?.code?.code) return;
    setWorking(true);
    try {
      const res = await axios.post(CODES_URL, { action: 'verify', code: current.code.code, athleteEmail: row.email },
        authHeaders);
      if (!res.data?.verified) throw new Error('not verified');
      toast.success(`${row.name || row.email} is verified on ${displayTeamName(row) || teamTitle || 'the team'}.`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Could not verify this athlete.');
    } finally {
      setWorking(false);
    }
  };

  // An alias stops linking (admin/team-codes/ 'alias_remove'): athletes on
  // it who enter the code are no longer moved onto the code's team.
  const unlink = async () => {
    const alias = unlinking;
    if (!alias || !current?.code?.code) return;
    setWorking(true);
    try {
      await axios.post(CODES_URL, { action: 'alias_remove', code: current.code.code, aliasTeamId: alias.teamId,
        aliasLeague: alias.league || '' }, authHeaders);
      toast.success(`${aliasLabel(alias)} no longer links with ${current.code.code}.`);
      setUnlinking(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Could not stop linking this listing.');
    } finally {
      setWorking(false);
    }
  };

  const code = current?.code;
  const athletes = current?.athletes || [];
  const program = programOf(current);
  const withoutCode = Array.isArray(current?.athletesWithoutCode) ? current.athletesWithoutCode : [];
  // The team a code links athletes to (and moves an alias's athletes onto):
  // the program for a program code, else this team.
  const codeTeam = (program.scope === 'program' && program.name) || teamTitle || 'the team';

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <Card className="flex w-full shrink-0 flex-col gap-4 lg:sticky lg:top-4 lg:w-[280px]">
        <Overline>Team scope</Overline>
        <CascadePicker
          filters={filters}
          onFiltersChange={setFilters}
          teams={teams}
          selectedTeam={selectedTeam}
          onTeamSelect={setSelectedTeam}
          isLoadingTeams={isLoadingTeams}
          layout="sidebar"
          variant="v2"
        />
      </Card>

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <Card className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <Overline>Selected team</Overline>
              {request ? (
                <>
                  <h3 className="mt-1 bm-h-m text-bm-ink">{teamTitle || 'Team'}</h3>
                  <p className="bm-body-m text-bm-muted">
                    {[request.leagueLabel, request.city, request.gender === 'F' ? 'Women/Girls' : request.gender === 'M' ? 'Men/Boys' : ''].filter(Boolean).join(' · ')}
                  </p>
                </>
              ) : (
                <p className="mt-1 bm-body-m text-bm-muted">Pick a sport, level and team in the rail. Every team CacheShare can list can get a code.</p>
              )}
            </div>
            {request && (
              <Badge tone="outline">{SOURCE_LABEL[request.source] || request.source}</Badge>
            )}
          </div>

          {request && (loadingCode ? <Loading label="Checking for a code..." /> : (
            <>
              {current?.problem && <p className="bm-body-m text-bm-critical">{current.problem}</p>}
              {code ? (
                <div className="flex flex-col gap-4 rounded-[8px] border border-bm-border bg-bm-subtle p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <Overline>Team code</Overline>
                      <button
                        type="button"
                        onClick={() => copy(code.code)}
                        title="Copy code"
                        className="mt-1 block font-mono text-[32px] leading-none tracking-[0.3em] text-bm-ink"
                      >
                        {code.code}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Btn variant="secondary" size="sm" onClick={() => copy(code.code)}>
                        <Icon name="copy" size={13} className="mr-1.5" /> {copied === code.code ? 'Copied' : 'Copy'}
                      </Btn>
                      <Btn variant="secondary" size="sm" onClick={() => setConfirmRegen(true)} disabled={working}>
                        <Icon name="refresh" size={13} className="mr-1.5" /> Regenerate
                      </Btn>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 bm-body-s text-bm-muted">
                    <span>Issued {fmtDate(code.createdAt)}{code.createdBy ? ` by ${code.createdBy}` : ''}</span>
                    <span>{code.uses || 0} {code.uses === 1 ? 'use' : 'uses'}</span>
                    {code.lastUsedAt && <span>Last used {fmtDate(code.lastUsedAt)}</span>}
                    {code.league && <span>League key {code.league}</span>}
                  </div>
                  {program.scope === 'program' && (
                    <p className="bm-body-s text-bm-ink" data-testid="program-scope">
                      One code for every age group of {program.name || request.teamName || 'the program'}
                      {program.squads.length > 0 ? `: ${squadAges(program.squads)}` : ''}. Each player picks their squad when they enter it.
                    </p>
                  )}
                  {program.aliases.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2" data-testid="aliases">
                      <span className="bm-label-xs text-bm-muted">Also links</span>
                      {program.aliases.map((a) => (
                        <span key={`${a.code || ''}|${a.teamId || ''}|${a.league || ''}`} className="inline-flex items-center gap-1">
                          <Badge tone="outline">{aliasLabel(a)}</Badge>
                          <button
                            type="button"
                            onClick={() => setUnlinking(a)}
                            disabled={working}
                            aria-label={`Stop linking ${aliasLabel(a)}`}
                            className="bm-label-xs text-bm-muted hover:text-bm-orange"
                          >
                            Remove
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {program.aliasCodes.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2" data-testid="alias-codes">
                      <span className="bm-label-xs text-bm-muted">Squad codes that still work</span>
                      {program.aliasCodes.map((a) => (
                        <Badge key={`${a.code || ''}|${a.teamId || ''}|${a.league || ''}`} tone="outline">{aliasLabel(a)}</Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Btn variant="secondary" size="sm" onClick={() => setListing('alias')} disabled={working}>Link another listing</Btn>
                    {program.scope === 'program' && (
                      <Btn variant="secondary" size="sm" onClick={() => setListing('squad')} disabled={working}>Add a missing age group</Btn>
                    )}
                  </div>
                  <p className="bm-body-s text-bm-muted">
                    Athletes enter this on the team code step of onboarding, or on their profile, and are linked and verified on {codeTeam} right away. Regenerating retires this code for anyone who has not used it yet.
                  </p>
                </div>
              ) : !current?.problem && (
                <EmptyState
                  action={(
                    <div className="flex flex-wrap justify-center gap-2">
                      <Btn onClick={() => act('issue')} disabled={working}>
                        <Icon name="key" size={14} className="mr-1.5" /> {working ? 'Issuing...' : 'Issue team code'}
                      </Btn>
                      {program.scope === 'program' && (
                        <Btn variant="secondary" onClick={() => act('issue', 'team')} disabled={working}>This squad only</Btn>
                      )}
                    </div>
                  )}
                >
                  No code yet for {teamTitle || 'this team'}. Issue one and share it with the coach or the players.
                  {program.scope === 'program' && ` It covers every age group of ${program.name || 'the program'}${
                    program.squads.length ? ` (${squadAges(program.squads)})` : ''}, unless you pick this squad only.`}
                </EmptyState>
              )}

              {code && (
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <Icon name="users" size={14} className="text-bm-muted" />
                    <span className="bm-label-xs text-bm-muted">Joined with this code · {athletes.length}</span>
                  </div>
                  {athletes.length === 0 ? (
                    <p className="bm-body-s text-bm-muted">No athlete has used this code yet.</p>
                  ) : (
                    <ul className="divide-y divide-bm-border rounded-[8px] border border-bm-border bg-white">
                      {athletes.map((a) => (
                        <li key={a.email} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                          <span className="min-w-0">
                            <span className="bm-body-m text-bm-ink">{a.name || a.email}</span>
                            {a.name && <span className="ml-2 bm-body-s text-bm-muted">{a.email}</span>}
                          </span>
                          <span className="flex items-center gap-2">
                            {ageGroupOf(a) && <Badge tone="outline">{ageGroupOf(a)}</Badge>}
                            <span className="bm-body-s text-bm-muted">{fmtDate(a.joinedAt)}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {code && withoutCode.length > 0 && (
                <div data-testid="without-code">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-2">
                      <Icon name="users" size={14} className="text-bm-muted" />
                      <span className="bm-label-xs text-bm-muted">Athletes on this program without the code · {withoutCode.length}</span>
                    </span>
                    <Btn variant="secondary" size="sm" onClick={() => emailCode(withoutCode)} disabled={working}>
                      <Icon name="mail" size={13} className="mr-1.5" /> Email them the code
                    </Btn>
                  </div>
                  <ul className="divide-y divide-bm-border rounded-[8px] border border-bm-border bg-white">
                    {withoutCode.map((a) => (
                      <li key={a.email} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                        <span className="min-w-0">
                          <span className="bm-body-m text-bm-ink">{a.name || a.email}</span>
                          {a.name && <span className="ml-2 bm-body-s text-bm-muted">{a.email}</span>}
                          {(displayTeamName(a) || ageGroupOf(a)) && (
                            <span className="block bm-body-s text-bm-muted">{displayTeamName(a) || ageGroupOf(a)}</span>
                          )}
                        </span>
                        <Btn variant="secondary" size="sm" onClick={() => verifyOne(a)} disabled={working}>Verify</Btn>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ))}

          {request && <TeamPlanBlock request={request} authHeaders={authHeaders} program={program} code={code} />}
        </Card>

        <Card padding="p-0" className="overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-bm-border px-5 py-4">
            <div>
              <Overline>Issued codes</Overline>
              <p className="bm-body-s text-bm-muted">Every active team code. Retired codes are not listed.</p>
            </div>
            <SearchBox value={q} onChange={setQ} placeholder="Team, league or code" className="w-full sm:w-[260px]" />
          </div>
          {loadingIssued && issued.length === 0 ? <Loading label="Loading codes..." /> : issued.length === 0 ? (
            <EmptyState className="m-5">{q ? 'No code matches that search.' : 'No team codes issued yet.'}</EmptyState>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead className="bg-bm-subtle">
                  <tr>
                    <th className={`${TH} !pl-5`}>Code</th>
                    <th className={TH}>Team</th>
                    <th className={TH}>League</th>
                    <th className={TH}>Source</th>
                    <th className={TH}>Uses</th>
                    <th className={TH}>Issued</th>
                  </tr>
                </thead>
                <tbody>
                  {issued.map((c) => (
                    <tr key={c.code} className="border-b border-bm-border bg-white bm-body-m text-bm-ink transition-colors last:border-0 hover:bg-bm-subtle">
                      <td className="px-5 py-2.5"><CodeChip code={c.code} onCopy={copy} copied={copied} /></td>
                      <td className="px-3 py-2.5">
                        <div>{displayTeamName(c) || c.teamId}</div>
                        {c.scope === 'program' ? (
                          <div className="bm-body-s text-bm-muted">
                            Every age group{squadAges(c.squads) ? `: ${squadAges(c.squads)}` : ''}
                          </div>
                        ) : c.city && <div className="bm-body-s text-bm-muted">{c.city}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div>{c.leagueLabel || c.league}</div>
                        {c.leagueLabel && <div className="bm-body-s text-bm-muted">{c.league}</div>}
                      </td>
                      <td className="px-3 py-2.5"><Badge tone="outline">{SOURCE_LABEL[c.teamSource] || c.teamSource}</Badge></td>
                      <td className="px-3 py-2.5">{c.uses || 0}</td>
                      <td className="px-3 py-2.5 bm-body-s text-bm-muted">{fmtDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {listing && code && (
        <CodeListingPicker
          kind={listing}
          code={code.code}
          teamName={codeTeam}
          authHeaders={authHeaders}
          onClose={() => setListing(null)}
          onDone={() => { setListing(null); reload(); }}
        />
      )}

      {unlinking && code && (
        <BmModal
          title="Stop linking this listing?"
          onClose={() => !working && setUnlinking(null)}
          width={480}
          footer={(
            <>
              <Btn variant="secondary" onClick={() => setUnlinking(null)} disabled={working}>Keep it</Btn>
              <Btn onClick={unlink} disabled={working}>Stop linking</Btn>
            </>
          )}
        >
          <p className="bm-body-m text-bm-ink">
            Athletes on {aliasLabel(unlinking)} who enter {code.code} are no longer moved onto {codeTeam}.
          </p>
        </BmModal>
      )}

      {confirmRegen && code && (
        <BmModal
          title="Regenerate this team code?"
          onClose={() => !working && setConfirmRegen(false)}
          width={480}
          footer={(
            <>
              <Btn variant="secondary" onClick={() => setConfirmRegen(false)} disabled={working}>Keep current code</Btn>
              <Btn onClick={() => act('regenerate', code.scope === 'team' ? 'team' : '')} disabled={working}>
                {working ? 'Issuing...' : 'Regenerate'}
              </Btn>
            </>
          )}
        >
          <p className="bm-body-m text-bm-ink">
            {code.code} stops working for anyone who has not used it yet. Athletes already linked with it stay on {codeTeam}.
          </p>
        </BmModal>
      )}
    </div>
  );
};

export default TeamCodesPanel;
